import React, { useState } from 'react';
import { trainingTemplatesAPI } from '../../services/api';
import type { TrainingPlanItem } from '../../types/trainingSession';
import type { TrainingTemplate } from '../../types/trainingTemplate';
import PlanBlocksEditor from '../trainings/PlanBlocksEditor';
import { cleanItems, emptyItem, hasUntitledContent, totalMinutes } from '../trainings/planUtils';
import DrillSheet from './DrillSheet';
import { getErrorMessage } from '../../utils/errorMessage';

interface TemplateFormModalProps {
  /** Szerkesztendő sablon; null új sablonhoz. */
  template: TrainingTemplate | null;
  /** Új sablonnál előtöltés (pl. egy edzés tervéből). */
  initial?: { goal?: string | null; items: TrainingPlanItem[] };
  onClose: () => void;
  onSaved: (template: TrainingTemplate) => void;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/** Edzéssablon létrehozása/szerkesztése: név, ajánlott korosztály, cél és a blokkok. */
const TemplateFormModal: React.FC<TemplateFormModalProps> = ({ template, initial, onClose, onSaved }) => {
  const [name, setName] = useState(template?.name ?? '');
  const [ageGroupHint, setAgeGroupHint] = useState(template?.ageGroupHint ?? '');
  const [goal, setGoal] = useState(template?.goal ?? initial?.goal ?? '');
  const [items, setItems] = useState<TrainingPlanItem[]>(() => {
    const source = template?.items ?? initial?.items ?? [];
    return source.length > 0 ? source.map((i) => ({ ...i })) : [emptyItem()];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Adj nevet a sablonnak.');
      return;
    }
    if (hasUntitledContent(items)) {
      setError('Minden blokknak adj nevet (pl. Bemelegítés).');
      return;
    }
    const cleaned = cleanItems(items);
    if (cleaned.length === 0 && !goal.trim()) {
      setError('Üres edzéstervből nem készíthető sablon.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload = { name: name.trim(), ageGroupHint: ageGroupHint.trim() || null, goal: goal.trim() || null, items: cleaned };
      const saved = template
        ? await trainingTemplatesAPI.update(template.id, payload)
        : await trainingTemplatesAPI.create(payload);
      onSaved(saved);
    } catch (err: any) {
      setError(getErrorMessage(err, 'A mentés nem sikerült.'));
    } finally {
      setSaving(false);
    }
  };

  const minutes = totalMinutes(items);

  return (
    <DrillSheet
      title={template ? 'Sablon szerkesztése' : 'Mentés edzéssablonként'}
      onClose={onClose}
      maxWidthClass="md:max-w-3xl"
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-gray-500">
            {items.filter((i) => i.title.trim()).length} blokk{minutes > 0 ? ` · ${minutes} perc` : ''}
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50">
              Mégse
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-md bg-primary-600 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Mentés...' : 'Sablon mentése'}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">Sablon neve *</label>
            <input
              id="template-name"
              type="text"
              maxLength={200}
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="pl. Keddi technikai edzés – passzjáték"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="template-age" className="block text-sm font-medium text-gray-700 mb-1">Ajánlott korosztály</label>
            <input
              id="template-age"
              type="text"
              maxLength={50}
              value={ageGroupHint}
              onChange={(e) => setAgeGroupHint(e.target.value)}
              placeholder="pl. U11-U14"
              className={inputClass}
            />
          </div>
        </div>

        <div>
          <label htmlFor="template-goal" className="block text-sm font-medium text-gray-700 mb-1">Az edzés célja</label>
          <input
            id="template-goal"
            type="text"
            maxLength={500}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="pl. Gyors kombinatív játék, átmenetek támadásból védekezésbe"
            className={inputClass}
          />
        </div>

        <div className="space-y-3 rounded-lg bg-gray-50 p-3">
          <PlanBlocksEditor items={items} setItems={setItems} onAiGoal={(aiGoal) => !goal.trim() && setGoal(aiGoal)} />
        </div>
      </div>
    </DrillSheet>
  );
};

export default TemplateFormModal;
